import { useLazyQuery } from '@apollo/client';
import { Box, Icon, IconSize, Menu, MenuOptions, Text, TextFieldSize, useModalState, Spinner } from '@bechtel/gds';
import {
  BlockStatusCode,
  CommodityMilestone,
  CommodityType,
  MaterialCommodity,
  MilestoneStatusCode,
  MilestoneType,
  Scalars,
  WorkPackage,
  MilestoneActivityStats,
  MilestoneActivityStatusType,
  MilestoneTypeCount,
  CommodityStatusCode,
} from '@bechtel/types';
import { capitalize, every, flatMap, map, some, toLower, uniqBy, filter, get } from 'lodash';
import React, { FC, Suspense, useCallback, useMemo, useState } from 'react';
import { GET_WORKPACKAGE_MILESTONE_STATS } from '@bechtel/client-gql';
import { SuspenseLoader } from '@bechtel/client-common';
import { useWorkPackagePermissions } from '~/hooks/use-work-package-permissions';
import { useWorkPackage } from '~/detail/context';
import { MenuButtonStyled, MenuItemStyled, PopperContainerStyled } from './styled';
import { MultipleCommodityAction } from './confirm/internals';
import { t, Trans } from '@lingui/macro';

interface CommodityBulkActionsProps {
  commodities: MaterialCommodity[];
  commodityType: CommodityType;
}

interface BulkActionOptions {
  commodityMilestones?: CommodityMilestone[];
  milestoneStatus?: MilestoneStatusCode;
  milestoneActivityStats: MilestoneActivityStats[];
}
interface BulkMenuOption {
  title: string;
  options: BulkActionOptions;
}

export interface ActionMilestone {
  action: string;
  milestoneTypeId?: MilestoneType['id'];
  milestoneName?: CommodityMilestone['name'];
  itrRequired: boolean;
}
interface CommonActionProps {
  commodityMilestones?: CommodityMilestone[];
  title: Scalars['String'];
  closeMenu: () => void;
  openConfirmModal: () => void;
  setActionMileStone: (ActionMilestone) => void;
  milestoneTypeCounts: MilestoneTypeCount[];
}
const CommonAction: FC<CommonActionProps> = ({
  title,
  closeMenu,
  openConfirmModal,
  setActionMileStone,
  milestoneTypeCounts,
}) => {
  const isApprovedAction = toLower(title) === t`approve`;

  const actionableMilestoneTypeCounts = filter(
    milestoneTypeCounts,
    (milestoneTypeCount) => milestoneTypeCount.numMilestones > 0
  );

  return (
    <>
      {map(actionableMilestoneTypeCounts, (option) => (
        <MenuItemStyled
          key={`${title}-${option.milestoneType.id}`}
          data-testid={`menu-item-${title}-${option.milestoneType.id}`}
          onMouseDown={(event) => {
            setActionMileStone({
              action: title,
              milestoneName: option.milestoneType.name,
              milestoneTypeId: option.milestoneType.id,
              itrRequired: false,
            });
            openConfirmModal();
            closeMenu(); //
            event.preventDefault();
          }}
        >
          {isApprovedAction && `${capitalize(title)} ${t`All Claimed`} ${option.milestoneType.name}`}
          {!isApprovedAction && `${capitalize(title)} ${t`All`} ${option.milestoneType.name}`}
        </MenuItemStyled>
      ))}
      <MenuItemStyled
        key={`${title}-item-all`}
        data-testid={`menu-item-all-${title}-`}
        onMouseDown={(event) => {
          setActionMileStone({
            action: title,
            milestoneTypeId: '' /* blank to represent all milestone */,
            milestoneName: '',
            itrRequired: false,
          });
          openConfirmModal();
          closeMenu(); //
          event.preventDefault();
        }}
      >
        {isApprovedAction && `${capitalize(title)} ${t`All Claimed Milestones`}`}
        {!isApprovedAction && `${capitalize(title)} ${t`All  Milestones`}`}
      </MenuItemStyled>
    </>
  );
};
export function useMenuState(initialState: boolean) {
  const [visible, setState] = useState(initialState);
  const open = useCallback(() => {
    setState(true);
  }, []);

  const close = useCallback(() => {
    setState(false);
  }, []);
  const toggle = useCallback(() => {
    setState(!visible);
  }, []);

  return { visible, open, close, toggle };
}
export const CommodityBulkActions: FC<CommodityBulkActionsProps> = ({ commodities, commodityType }) => {
  const { assignedCrewMembers, id: workPackageId } = useWorkPackage();
  const { canClaimMilestone, canApproveMilestone } = useWorkPackagePermissions(assignedCrewMembers);
  const { visible, open: openConfirmModal, close: closeConfirmModal } = useModalState(false);
  const [actionMilestone, setActionMileStone] = useState<ActionMilestone>();
  const { visible: visibleClaimAllList, toggle: toggleClaimAllList, close: closeClaimAllList } = useMenuState(false);
  const { visible: visibleApproveAllList, toggle: toggleApproveAllList, close: closeApprovedAllList } = useMenuState(
    false
  );

  const [loadMilestoneActivityStats, { data, loading, error }] = useLazyQuery<{ workPackagesByIds: WorkPackage[] }>(
    GET_WORKPACKAGE_MILESTONE_STATS,
    { fetchPolicy: 'network-only', notifyOnNetworkStatusChange: true }
  );

  const milestoneStats: MilestoneActivityStats[] = get(
    data,
    'workPackagesByIds[0].milestoneActivityStats[0].milestoneActivityStats',
    []
  );

  const claimableMilestoneTypes = filter(
    milestoneStats,
    (milestoneStat) => milestoneStat.milestoneActivityStatus === MilestoneActivityStatusType.CLAIMABLE
  );
  const approvableMilestoneTypes = filter(
    milestoneStats,
    (milestoneStat) => milestoneStat.milestoneActivityStatus === MilestoneActivityStatusType.APPROVABLE
  );
  const pendingApproveMilestone = uniqBy(
    flatMap(
      commodities.map((commodity: MaterialCommodity) => {
        return commodity.milestones.filter((milestone) => {
          return milestone.currentStatus.codeI18n === MilestoneStatusCode.CLAIMED;
        });
      })
    ),
    'name'
  );

  const pendingClaimMilestone = uniqBy(
    flatMap(
      commodities.map((commodity: MaterialCommodity) => {
        return commodity.milestones.filter((milestone) => {
          const hasOpenBlocks = some(milestone.blocks, { currentStatusI18n: BlockStatusCode.BLOCK_OPENED });
          return (
            !hasOpenBlocks &&
            milestone.currentStatus.codeI18n !== MilestoneStatusCode.CLAIMED &&
            milestone.currentStatus.codeI18n !== MilestoneStatusCode.REJECTED &&
            milestone.currentStatus.codeI18n !== MilestoneStatusCode.APPROVED &&
            (milestone.weldStatuses && milestone.weldStatuses.length > 0
              ? every(milestone.weldStatuses, (weldStatus) => {
                  return weldStatus?.status?.status === CommodityStatusCode.VT_APPROVED;
                })
              : true)
          );
        });
      })
    ),
    'name'
  );

  const menuOptions: BulkMenuOption[] = useMemo(() => {
    const menuItems: BulkMenuOption[] = [];
    if (canApproveMilestone) {
      menuItems.push({
        title: t`Approve`,
        options: {
          commodityMilestones: [...pendingApproveMilestone],
          milestoneActivityStats: approvableMilestoneTypes,
        },
      });
    } else if (canClaimMilestone) {
      menuItems.push({
        title: t`Claim`,
        options: { commodityMilestones: [...pendingClaimMilestone], milestoneActivityStats: claimableMilestoneTypes },
      });
    }

    return menuItems;
  }, [canApproveMilestone, canClaimMilestone, commodities]);

  return (
    <>
      {/* TODO comment to use reference and popper references, when the GDS menu flickering is fixed.*/}
      <Menu>
        {({ isOpen, toggle, close: closeMenu }) => (
          <div
            onBlur={() => {
              closeMenu();
              closeApprovedAllList();
              closeClaimAllList();
            }}
          >
            <MenuButtonStyled
              data-testid="claim-approve-button"
              onClick={() => {
                if (!isOpen) {
                  loadMilestoneActivityStats({
                    variables: {
                      ids: [workPackageId],
                      input: { commodityTypeIds: [commodityType.id] },
                    },
                  });
                }
                toggle();
                closeApprovedAllList();
                closeClaimAllList();
              }}
              size={TextFieldSize.SMALL}
              isDisabled={(!canApproveMilestone && pendingClaimMilestone.length < 1) || loading || error}
            >
              <Box justifyContent={{ sm: 'center' }} alignItems={{ sm: 'center' }} mr={{ sm: '300' }}>
                {loading ? (
                  <Spinner />
                ) : (
                  <Icon icon={canApproveMilestone ? 'ellipsis-v' : 'plus'} size={IconSize.SMALL} fillColor="blue.800" />
                )}
              </Box>
              <Text size="300" color="grey.1000" style={{ whiteSpace: 'nowrap' }}>
                {canApproveMilestone ? t`More` : t`Claim All`}
              </Text>
            </MenuButtonStyled>
            <PopperContainerStyled>
              {isOpen && (
                <MenuOptions
                  css={`
                    order: 2;
                    border-bottom-left-radius: 0px;
                    border-bottom-right-radius: 0px;
                  `}
                >
                  <Box flexDirection={{ sm: 'row' }}>
                    <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }} pt={{ sm: '400' }}>
                      {canApproveMilestone ? (
                        <>
                          <MenuItemStyled
                            isActive={visibleClaimAllList}
                            data-testid="foreman-rights"
                            onMouseDown={(event) => {
                              toggleClaimAllList();
                              closeApprovedAllList();
                              event.preventDefault();
                            }}
                            isDisabled={pendingClaimMilestone.length < 1}
                          >
                            <Trans>Claim All</Trans>
                          </MenuItemStyled>
                          <MenuItemStyled
                            isActive={visibleApproveAllList}
                            onMouseDown={(event) => {
                              toggleApproveAllList();
                              closeClaimAllList();
                              event.preventDefault();
                            }}
                            isDisabled={pendingApproveMilestone.length < 1}
                          >
                            <Trans> Approve All</Trans>
                          </MenuItemStyled>
                        </>
                      ) : (
                        canClaimMilestone &&
                        pendingClaimMilestone && (
                          <CommonAction
                            title={t`Claim`}
                            commodityMilestones={pendingClaimMilestone}
                            milestoneTypeCounts={claimableMilestoneTypes[0]?.stats}
                            closeMenu={closeMenu}
                            openConfirmModal={openConfirmModal}
                            setActionMileStone={setActionMileStone}
                          />
                        )
                      )}
                    </Box>
                  </Box>
                </MenuOptions>
              )}
              {(visibleClaimAllList || visibleApproveAllList) && isOpen && (
                <MenuOptions
                  css={`
                    order: 1;
                    border-bottom-left-radius: 0px;
                    border-bottom-right-radius: 0px;
                  `}
                >
                  <Box flexDirection={{ sm: 'row' }}>
                    <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }} pt={{ sm: '400' }}>
                      {visibleClaimAllList && pendingClaimMilestone && (
                        <CommonAction
                          title={t`Claim`}
                          commodityMilestones={pendingClaimMilestone}
                          milestoneTypeCounts={claimableMilestoneTypes[0]?.stats}
                          closeMenu={closeMenu}
                          openConfirmModal={openConfirmModal}
                          setActionMileStone={setActionMileStone}
                        />
                      )}
                      {visibleApproveAllList && pendingApproveMilestone && (
                        <CommonAction
                          title={t`Approve`}
                          commodityMilestones={pendingApproveMilestone}
                          milestoneTypeCounts={approvableMilestoneTypes[0]?.stats}
                          closeMenu={closeMenu}
                          openConfirmModal={openConfirmModal}
                          setActionMileStone={setActionMileStone}
                        />
                      )}
                    </Box>
                  </Box>
                </MenuOptions>
              )}
            </PopperContainerStyled>
          </div>
        )}
      </Menu>
      {visible && actionMilestone && (
        <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
          <MultipleCommodityAction
            close={closeConfirmModal}
            actionMilestone={actionMilestone}
            commodities={commodities}
            commodityType={commodityType}
          ></MultipleCommodityAction>
        </Suspense>
      )}
    </>
  );
};
